import type { KeyboardEventHandler } from 'react';
import { useState } from 'react';
import styled, { css } from 'styled-components';

import { InputWrapper, RequirementCheck } from '../atoms';

type TRegex = boolean | undefined;

const RequirementsWrapper = styled.div(
  ({ theme }) => css`
    border-top: ${theme.borders.light};
  `
);

export const NewPasswordFields = ({
  register,
  onChange = () => undefined,
  errors = {}
}) => {
  const [state, setState] = useState({ newPassword: '', confirmPassword: '' });

  const regexEight = new RegExp('^.{8,}$');
  const regexLowerAndUpper = new RegExp('^(?=.*[a-z])(?=.*[A-Z])');
  const regexNumber = new RegExp('^(?=.*[0-9])');
  const regexSpecial = new RegExp('^(?=.*[!@#$%^&*])');

  const [hasEight, setHasEight] = useState<TRegex>(undefined);
  const [hasLowerAndUpper, setHasLowerAndUpper] = useState<TRegex>(undefined);
  const [hasNumber, setHasNumber] = useState<TRegex>(undefined);
  const [hasSpecial, setHasSpecial] = useState<TRegex>(undefined);
  const [passwordsMatch, setPasswordsMatch] = useState<TRegex>(undefined);

  const handleChange: KeyboardEventHandler<HTMLInputElement> = (e) => {
    const { name, value } = e.currentTarget;
    const _state = {
      ...state,
      [name]: value
    };

    const eight = regexEight.test(_state.newPassword);
    const lowerAndUpper = regexLowerAndUpper.test(_state.newPassword);
    const numbered = regexNumber.test(_state.newPassword);
    const special = regexSpecial.test(_state.newPassword);
    const matching =
      _state.newPassword && _state.newPassword === _state.confirmPassword;

    setHasEight(eight);
    setHasLowerAndUpper(lowerAndUpper);
    setHasNumber(numbered);
    setHasSpecial(special);
    setState(_state);
    setPasswordsMatch(matching);

    let newPasswordError;
    let confirmPasswordError;
    if (!eight) {
      newPasswordError = 'Your password must be at least 8 characters';
    } else if (!lowerAndUpper) {
      newPasswordError =
        'Your password must have at least 1 uppercase and lowercase character';
    } else if (!numbered) {
      newPasswordError = 'Your password must have at least 1 number';
    } else if (!special) {
      newPasswordError = 'Your password must have at least 1 special character';
    }
    if (!matching) {
      confirmPasswordError = 'Passwords do not match';
    }

    onChange(_state, {
      newPassword: newPasswordError,
      confirmPassword: confirmPasswordError
    });
  };

  return (
    <>
      <InputWrapper
        name="newPassword"
        type="password"
        label="New Password"
        required
        autoFill={false}
        register={register}
        onKeyUp={handleChange}
        errorMessage={errors.newPassword?.message}
      />
      <InputWrapper
        name="confirmPassword"
        type="password"
        label="Confirm New Password"
        required
        autoFill={false}
        register={register}
        onKeyUp={handleChange}
        errorMessage={errors.confirmPassword?.message}
      />
      <RequirementsWrapper>
        <p>
          <strong>Your password must:</strong>
        </p>
        <RequirementCheck
          label="Be a minimum length of 8 characters"
          checked={hasEight}
        />
        <RequirementCheck
          label="Include at least one number"
          checked={hasNumber}
        />
        <RequirementCheck
          label="Include at least one uppercase and lower case letter"
          checked={hasLowerAndUpper}
        />
        <RequirementCheck
          label="Include at least one special character"
          checked={hasSpecial}
        />
        <RequirementCheck
          label="Passwords must match"
          checked={passwordsMatch}
        />
      </RequirementsWrapper>
    </>
  );
};
